import React, { useEffect, useState, useCallback } from 'react';
import superAdminApi from '../../utils/superAdminApi';
import toast from 'react-hot-toast';
import { FiPlus, FiEdit2, FiTrash2, FiPlusCircle, FiSliders } from 'react-icons/fi';
import { fmtDateTime } from './activityMeta';

// Cloth rolls the cutting staff cut from. Rolls are added / restocked here;
// every metre a cutter records on an order in the app comes off the roll, so
// "Remaining" is always the live stock.
const ROLL_TYPES = ['Shirt', 'Pant', 'Uniform', 'Other'];
const TABS = [
  { key: 'rolls', label: 'Cloth Rolls' },
  { key: 'jobs', label: 'Cutting Jobs' },
  { key: 'history', label: 'Stock History' },
];
const KIND_META = {
  opening: { label: 'New roll', color: '#6366f1' },
  restock: { label: 'Restock', color: '#10b981' },
  adjustment: { label: 'Correction', color: '#f59e0b' },
  cutting: { label: 'Cut for order', color: '#0891b2' },
  cutting_undo: { label: 'Cut undone', color: '#94a3b8' },
};
const STATUS_META = {
  assigned: { label: 'Assigned', cls: 'badge-gray' },
  in_progress: { label: 'Cutting', cls: 'badge-warning' },
  done: { label: 'Done', cls: 'badge-success' },
};
const m2 = (n) => `${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })} m`;
const emptyRoll = { name: '', type: 'Shirt', color: '', code: '', meters: '', minAlertMeters: 5, note: '' };

export default function SuperAdminFabric() {
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState('');
  const [tab, setTab] = useState('rolls');
  const [loading, setLoading] = useState(true);

  const [rolls, setRolls] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobStatus, setJobStatus] = useState('active');
  const [movements, setMovements] = useState([]);
  const [historyRoll, setHistoryRoll] = useState('');

  const [rollModal, setRollModal] = useState(null); // { mode: 'add'|'edit', form, id }
  const [stockModal, setStockModal] = useState(null); // { roll, mode: 'add'|'set', meters, reason }
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    superAdminApi.get('/shops').then(r => {
      setShops(r.data.shops);
      if (r.data.shops.length > 0) setShopId(r.data.shops[0]._id);
      else setLoading(false);
    }).catch(() => toast.error('Failed to load shops'));
  }, []);

  const load = useCallback(async () => {
    if (!shopId) return;
    setLoading(true);
    try {
      if (tab === 'rolls') {
        const r = await superAdminApi.get('/fabric/rolls', { params: { shopId } });
        setRolls(r.data.rolls);
      } else if (tab === 'jobs') {
        const r = await superAdminApi.get('/fabric/jobs', { params: { shopId, status: jobStatus } });
        setJobs(r.data.jobs);
      } else {
        const [m, r] = await Promise.all([
          superAdminApi.get('/fabric/movements', { params: { shopId, rollId: historyRoll || undefined } }),
          superAdminApi.get('/fabric/rolls', { params: { shopId, includeInactive: 1 } }),
        ]);
        setMovements(m.data.movements);
        setRolls(r.data.rolls);
      }
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  }, [shopId, tab, jobStatus, historyRoll]);

  useEffect(() => { load(); }, [load]);

  const saveRoll = async () => {
    const f = rollModal.form;
    if (!f.name.trim()) return toast.error('Enter a roll name');
    setSaving(true);
    try {
      if (rollModal.mode === 'add') {
        await superAdminApi.post('/fabric/rolls', { ...f, shopId });
        toast.success('Roll added');
      } else {
        const { meters, ...details } = f;
        await superAdminApi.put(`/fabric/rolls/${rollModal.id}`, details);
        toast.success('Roll updated');
      }
      setRollModal(null);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    setSaving(false);
  };

  const saveStock = async () => {
    const meters = parseFloat(stockModal.meters);
    if (!(meters >= 0) || (stockModal.mode === 'add' && meters <= 0)) return toast.error('Enter valid metres');
    setSaving(true);
    try {
      await superAdminApi.post(`/fabric/rolls/${stockModal.roll._id}/stock`, {
        mode: stockModal.mode, meters, reason: stockModal.reason,
      });
      toast.success(stockModal.mode === 'add' ? `Added ${meters} m` : 'Stock corrected');
      setStockModal(null);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    setSaving(false);
  };

  const removeRoll = async (r) => {
    if (!window.confirm(`Remove roll "${r.name}"? It disappears from the cutters' list; its history is kept.`)) return;
    try {
      await superAdminApi.delete(`/fabric/rolls/${r._id}`);
      toast.success('Roll removed');
      load();
    } catch { toast.error('Delete failed'); }
  };

  // Totals per cloth type for the summary cards.
  const byType = rolls.filter(r => r.isActive).reduce((acc, r) => {
    const t = r.type || 'Other';
    acc[t] = acc[t] || { remaining: 0, used: 0, count: 0 };
    acc[t].remaining += r.remainingMeters || 0;
    acc[t].used += r.usedMeters || 0;
    acc[t].count += 1;
    return acc;
  }, {});
  const lowCount = rolls.filter(r => r.isActive && r.remainingMeters <= (r.minAlertMeters || 0)).length;

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <label className="form-label" style={{ marginBottom: 0 }}>Shop</label>
          <select className="form-control" style={{ width: 220 }} value={shopId} onChange={e => setShopId(e.target.value)}>
            {shops.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>
          <div style={{ flex: 1 }} />
          {tab === 'rolls' && (
            <button className="btn btn-accent" disabled={!shopId}
              onClick={() => setRollModal({ mode: 'add', form: { ...emptyRoll } })}>
              <FiPlus size={15} /> Add Cloth Roll
            </button>
          )}
        </div>
      </div>

      <div className="filter-tabs" style={{ marginBottom: 16 }}>
        {TABS.map(t => (
          <button key={t.key} className={`filter-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {loading ? <div className="loading"><div className="spinner"></div></div> : (
        <>
          {tab === 'rolls' && (
            <>
              <div className="stats-grid" style={{ marginBottom: 16 }}>
                {Object.entries(byType).map(([t, v]) => (
                  <div key={t} className="stat-card">
                    <div className="stat-value">{m2(v.remaining)}</div>
                    <div className="stat-label">{t} cloth left · {v.count} roll{v.count === 1 ? '' : 's'} · {m2(v.used)} cut</div>
                  </div>
                ))}
                <div className="stat-card">
                  <div className="stat-value" style={{ color: lowCount ? '#ef4444' : undefined }}>{lowCount}</div>
                  <div className="stat-label">Rolls running low</div>
                </div>
              </div>

              <div className="card">
                <div className="table-container">
                  <table>
                    <thead>
                      <tr><th>Roll</th><th>Type</th><th>Total added</th><th>Cut so far</th><th>Remaining</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {rolls.length === 0 && (
                        <tr><td colSpan={6}><div className="empty-state">
                          <h3>No cloth rolls yet</h3>
                          <p>Add the shop's shirt / pant / uniform rolls with their metres. Cutters then record what they cut and it comes off here.</p>
                        </div></td></tr>
                      )}
                      {rolls.map(r => {
                        const low = r.remainingMeters <= (r.minAlertMeters || 0);
                        return (
                          <tr key={r._id}>
                            <td>
                              <div style={{ fontWeight: 600 }}>{r.name}</div>
                              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                {[r.color, r.code && `#${r.code}`, r.note].filter(Boolean).join(' · ') || '—'}
                              </div>
                            </td>
                            <td><span className="badge badge-gray">{r.type}</span></td>
                            <td>{m2(r.totalMeters)}</td>
                            <td>{m2(r.usedMeters)}</td>
                            <td>
                              <strong style={{ color: low ? '#ef4444' : '#0f172a' }}>{m2(r.remainingMeters)}</strong>
                              {low && <div style={{ fontSize: 11, color: '#ef4444' }}>Low — alert at {m2(r.minAlertMeters)}</div>}
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                <button className="btn btn-outline btn-sm" title="New cloth arrived"
                                  onClick={() => setStockModal({ roll: r, mode: 'add', meters: '', reason: '' })}>
                                  <FiPlusCircle size={13} /> Add metres
                                </button>
                                <button className="btn btn-outline btn-sm btn-icon" title="Correct stock after a physical count"
                                  onClick={() => setStockModal({ roll: r, mode: 'set', meters: String(r.remainingMeters ?? ''), reason: '' })}>
                                  <FiSliders size={13} />
                                </button>
                                <button className="btn btn-outline btn-sm btn-icon" title="Edit details"
                                  onClick={() => setRollModal({ mode: 'edit', id: r._id, form: { name: r.name, type: r.type, color: r.color || '', code: r.code || '', minAlertMeters: r.minAlertMeters, note: r.note || '' } })}>
                                  <FiEdit2 size={13} />
                                </button>
                                <button className="btn btn-outline btn-sm btn-icon" title="Remove"
                                  style={{ color: '#ef4444', borderColor: '#fecaca' }} onClick={() => removeRoll(r)}>
                                  <FiTrash2 size={13} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {tab === 'jobs' && (
            <div className="card">
              <div className="card-header">
                <div className="filter-tabs" style={{ margin: 0 }}>
                  {[['active', 'Open'], ['done', 'Done'], ['all', 'All']].map(([k, l]) => (
                    <button key={k} className={`filter-tab ${jobStatus === k ? 'active' : ''}`} onClick={() => setJobStatus(k)}>{l}</button>
                  ))}
                </div>
                <span style={{ color: '#64748b', fontSize: 13 }}>
                  {jobs.length} orders · {m2(jobs.reduce((s, j) => s + (j.totalMeters || 0), 0))} cloth cut
                </span>
              </div>
              <div className="table-container">
                <table>
                  <thead>
                    <tr><th>Customer</th><th>Order</th><th>Cutter</th><th>Status</th><th>Cloth used</th><th>Delivery</th></tr>
                  </thead>
                  <tbody>
                    {jobs.length === 0 && (
                      <tr><td colSpan={6}><div className="empty-state">
                        <h3>No cutting jobs</h3>
                        <p>The shop admin sends an order to a cutter from the order screen in the app.</p>
                      </div></td></tr>
                    )}
                    {jobs.map(j => (
                      <tr key={j.measurementId}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{j.customerName}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{j.customerPhone}</div>
                        </td>
                        <td style={{ fontSize: 13 }}>
                          {(j.type || []).join(' + ') || '—'} × {j.suitQuantity || 1}
                          {j.clothLabel && <div style={{ fontSize: 11, color: '#94a3b8' }}>{j.clothLabel}</div>}
                        </td>
                        <td>
                          {j.cutting.assignedToName || '—'}
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>since {fmtDateTime(j.cutting.assignedAt)}</div>
                        </td>
                        <td>
                          <span className={`badge ${STATUS_META[j.cutting.status]?.cls || 'badge-gray'}`}>{STATUS_META[j.cutting.status]?.label || j.cutting.status}</span>
                          {j.cutting.completedAt && <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtDateTime(j.cutting.completedAt)}</div>}
                        </td>
                        <td>
                          <strong>{m2(j.totalMeters)}</strong>
                          {(j.cutting.fabricUsed || []).map(f => (
                            <div key={f._id} style={{ fontSize: 11, color: '#64748b' }}>{m2(f.meters)} · {f.rollName}</div>
                          ))}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {j.deliveryDate ? new Date(j.deliveryDate).toLocaleDateString('en-GB', { timeZone: 'Asia/Riyadh', day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                          {j.isDelivered && <div><span className="badge badge-success">Delivered</span></div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'history' && (
            <div className="card">
              <div className="card-header">
                <select className="form-control" style={{ width: 260 }} value={historyRoll} onChange={e => setHistoryRoll(e.target.value)}>
                  <option value="">All rolls</option>
                  {rolls.map(r => <option key={r._id} value={r._id}>{r.name}{!r.isActive ? ' (removed)' : ''}</option>)}
                </select>
                <span style={{ color: '#64748b', fontSize: 13 }}>latest {movements.length}</span>
              </div>
              <div className="table-container">
                <table>
                  <thead>
                    <tr><th>When (Saudi time)</th><th>Roll</th><th>What</th><th>Metres</th><th>Left after</th><th>For</th><th>By</th></tr>
                  </thead>
                  <tbody>
                    {movements.length === 0 && <tr><td colSpan={7}><div className="empty-state"><h3>No stock changes yet</h3></div></td></tr>}
                    {movements.map(mv => (
                      <tr key={mv._id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(mv.createdAt)}</td>
                        <td>{mv.rollName}<div style={{ fontSize: 11, color: '#94a3b8' }}>{mv.rollType}</div></td>
                        <td>
                          <span className="badge" style={{ background: `${KIND_META[mv.kind]?.color}22`, color: KIND_META[mv.kind]?.color }}>{KIND_META[mv.kind]?.label || mv.kind}</span>
                          {mv.reason && <div style={{ fontSize: 11, color: '#94a3b8' }}>{mv.reason}</div>}
                        </td>
                        <td style={{ fontWeight: 700, color: mv.meters < 0 ? '#ef4444' : '#10b981' }}>
                          {mv.meters > 0 ? '+' : ''}{m2(mv.meters)}
                        </td>
                        <td>{m2(mv.newMeters)}</td>
                        <td>{mv.customerName ? <>{mv.customerName}{mv.billNumber && <div style={{ fontSize: 11, color: '#94a3b8' }}>Bill #{mv.billNumber}</div>}</> : '—'}</td>
                        <td>{mv.byName || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {rollModal && (
        <div className="modal-overlay" onClick={() => setRollModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{rollModal.mode === 'add' ? '🧵 Add Cloth Roll' : '✏️ Edit Roll'}</h3>
              <button className="btn btn-outline btn-sm btn-icon" onClick={() => setRollModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Roll name</label>
                <input className="form-control" placeholder="e.g. White shirt cloth" autoFocus value={rollModal.form.name}
                  onChange={e => setRollModal({ ...rollModal, form: { ...rollModal.form, name: e.target.value } })} />
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Type</label>
                  <select className="form-control" value={rollModal.form.type}
                    onChange={e => setRollModal({ ...rollModal, form: { ...rollModal.form, type: e.target.value } })}>
                    {[...new Set([...ROLL_TYPES, rollModal.form.type])].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Colour (optional)</label>
                  <input className="form-control" value={rollModal.form.color}
                    onChange={e => setRollModal({ ...rollModal, form: { ...rollModal.form, color: e.target.value } })} />
                </div>
              </div>
              <div className="form-row-2">
                {rollModal.mode === 'add' && (
                  <div className="form-group">
                    <label className="form-label">Metres in this roll</label>
                    <input type="number" min="0" step="0.1" className="form-control" value={rollModal.form.meters}
                      onChange={e => setRollModal({ ...rollModal, form: { ...rollModal.form, meters: e.target.value } })} />
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Low-stock alert at (m)</label>
                  <input type="number" min="0" step="0.5" className="form-control" value={rollModal.form.minAlertMeters}
                    onChange={e => setRollModal({ ...rollModal, form: { ...rollModal.form, minAlertMeters: e.target.value } })} />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Roll / lot no. (optional)</label>
                  <input className="form-control" value={rollModal.form.code}
                    onChange={e => setRollModal({ ...rollModal, form: { ...rollModal.form, code: e.target.value } })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Note (optional)</label>
                  <input className="form-control" value={rollModal.form.note}
                    onChange={e => setRollModal({ ...rollModal, form: { ...rollModal.form, note: e.target.value } })} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setRollModal(null)}>Cancel</button>
              <button className="btn btn-accent" onClick={saveRoll} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {stockModal && (
        <div className="modal-overlay" onClick={() => setStockModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{stockModal.mode === 'add' ? '➕ Add metres' : '📏 Correct stock'}: {stockModal.roll.name}</h3>
              <button className="btn btn-outline btn-sm btn-icon" onClick={() => setStockModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: 12, color: '#64748b' }}>Now on the shelf: <strong>{m2(stockModal.roll.remainingMeters)}</strong></div>
              <div className="form-group">
                <label className="form-label">{stockModal.mode === 'add' ? 'Metres that arrived' : 'Actual metres left (after counting)'}</label>
                <input type="number" min="0" step="0.1" className="form-control" autoFocus value={stockModal.meters}
                  onChange={e => setStockModal({ ...stockModal, meters: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Reason (optional)</label>
                <input className="form-control" placeholder={stockModal.mode === 'add' ? 'e.g. New bundle from supplier' : 'e.g. Monthly count'} value={stockModal.reason}
                  onChange={e => setStockModal({ ...stockModal, reason: e.target.value })} />
              </div>
              {stockModal.meters !== '' && !isNaN(parseFloat(stockModal.meters)) && (
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  New remaining: <strong>{m2(stockModal.mode === 'add' ? (stockModal.roll.remainingMeters || 0) + parseFloat(stockModal.meters) : parseFloat(stockModal.meters))}</strong>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setStockModal(null)}>Cancel</button>
              <button className="btn btn-accent" onClick={saveStock} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
